import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";

const DEFAULT_LIVE_RELOAD_OCCURENCE_TIME = 10;
const LIVE_RELOAD_SEARCH_KEY = "auto-refresh";

function useLiveReload({ key, onReload, onSetAutoRefresh, force = false }) {
  const location = useLocation();
  const history = useHistory();
  const [disabled, setDisabled] = useState(false);

  const liveReloadActive = useMemo(() => {
    return location?.search?.includes(LIVE_RELOAD_SEARCH_KEY);
  }, [location]);

  const onActivateLiveReload = useCallback(() => {
    history.push(
      `${location.pathname}?auto-refresh=${DEFAULT_LIVE_RELOAD_OCCURENCE_TIME}`
    );
    window.location.reload();
  }, [location.pathname, history]);

  const onCancelLiveReload = useCallback(() => {
    clearInterval(window[key]);
    history.push(location.pathname);
    setDisabled(true);
  }, [location.pathname, history, key]);

  useEffect(() => {
    function initializeAutoRefresh() {
      const searchParameters = queryString.parse(location.search);

      let autoRefresh = parseInt(
        searchParameters?.[LIVE_RELOAD_SEARCH_KEY],
        10
      );

      if (force && Number.isNaN(autoRefresh) && !disabled) {
        onActivateLiveReload();
      }
      if (autoRefresh && autoRefresh < 5) {
        autoRefresh = 5;
        history.push(`${location.pathname}?auto-refresh=5`);
      }

      if (autoRefresh && !window?.[key]) {
        onSetAutoRefresh(true);
        window[key] = setInterval(onReload, autoRefresh * 1000);
      }
    }

    initializeAutoRefresh();

    return () => {
      clearInterval(window[key]);
    };
  }, [
    key,
    location.pathname,
    onReload,
    location.search,
    onSetAutoRefresh,
    history,
    force,
    onActivateLiveReload,
    disabled,
  ]);

  return { liveReloadActive, onActivateLiveReload, onCancelLiveReload };
}

export default useLiveReload;
